import { useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { FaWhatsapp } from 'react-icons/fa'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { whatsappLink } from '@/data/hotel'
import { gsap, ScrollTrigger, useGSAP } from '@/lib/gsap'
import { cn } from '@/lib/utils'

const NAV_LINKS = [
  { to: '/', label: 'Início' },
  { to: '/sobre', label: 'Sobre' },
  { to: '/galeria', label: 'Quartos' },
  { to: '/servicos', label: 'Serviços' },
  { to: '/contato', label: 'Contato' },
]

function Logo({ light }) {
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <img src="/images/logo.png" alt="" className="h-9 w-auto rounded-md" />
      <span
        className={cn(
          'font-heading text-2xl font-bold transition-colors duration-500',
          light ? 'text-ink-foreground' : 'text-foreground'
        )}
      >
        Star Hotel
      </span>
    </Link>
  )
}

function Header() {
  const header = useRef(null)
  const { pathname } = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  // Sobre o hero da Home o cabeçalho fica transparente, com texto claro
  const light = pathname === '/' && !isScrolled

  useGSAP(
    () => {
      let hidden = false
      ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate: (self) => {
          const y = self.scroll()
          setIsScrolled(y > 12)
          // Some ao rolar para baixo e volta ao rolar para cima
          const shouldHide = self.direction === 1 && y > 240
          if (shouldHide !== hidden) {
            hidden = shouldHide
            gsap.to(header.current, {
              yPercent: hidden ? -100 : 0,
              duration: hidden ? 0.35 : 0.5,
              ease: hidden ? 'power2.in' : 'power3.out',
              overwrite: true,
            })
          }
        },
      })
      setIsScrolled(window.scrollY > 12)
    },
    { scope: header }
  )

  return (
    <header
      ref={header}
      className={cn(
        'fixed inset-x-0 top-0 z-40 w-full border-b transition-[background-color,border-color] duration-500',
        light
          ? 'border-transparent bg-transparent'
          : 'border-border bg-background/85 backdrop-blur-md'
      )}
    >
      <nav className="container-page flex h-16 items-center justify-between gap-6">
        <Logo light={light} />

        {/* Menu desktop */}
        <div className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.to === '/'}>
              {({ isActive }) => (
                <Button
                  asChild
                  variant={isActive ? 'secondary' : 'ghost'}
                  size="lg"
                  className={cn(
                    'px-3.5',
                    light && !isActive && 'text-ink-foreground hover:bg-ink-foreground/15 hover:text-ink-foreground'
                  )}
                >
                  <span>{link.label}</span>
                </Button>
              )}
            </NavLink>
          ))}
        </div>

        <Button
          asChild
          size="lg"
          variant={light ? 'highlight' : 'default'}
          className="hidden rounded-full px-4 md:inline-flex"
        >
          <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
            <FaWhatsapp data-icon="inline-start" />
            Reservar
          </a>
        </Button>

        {/* Menu mobile */}
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon-lg"
              className={cn('md:hidden', light && 'text-ink-foreground hover:bg-ink-foreground/15 hover:text-ink-foreground')}
              aria-label="Abrir menu"
            >
              <Menu />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80">
            <SheetHeader>
              <SheetTitle className="font-heading text-xl">Star Hotel</SheetTitle>
              <SheetDescription>Elegância e conforto em Limoeiro-PE</SheetDescription>
            </SheetHeader>
            <Separator />
            <div className="flex flex-col gap-1 px-4">
              {NAV_LINKS.map((link) => (
                <SheetClose asChild key={link.to}>
                  <NavLink to={link.to} end={link.to === '/'}>
                    {({ isActive }) => (
                      <Button
                        asChild
                        variant={isActive ? 'secondary' : 'ghost'}
                        size="lg"
                        className="w-full justify-start text-base"
                      >
                        <span>{link.label}</span>
                      </Button>
                    )}
                  </NavLink>
                </SheetClose>
              ))}
            </div>
            <SheetFooter>
              <Button asChild variant="whatsapp" size="xl">
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp data-icon="inline-start" />
                  Reservar pelo WhatsApp
                </a>
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  )
}

export default Header
